import { Fa, TagInput, Toggle } from '@eltoro-ui/components'
import { useState } from 'react'
import Select, {
  ClearIndicatorProps,
  components,
  DropdownIndicatorProps,
  MultiValueRemoveProps,
} from 'react-select'
import { reactSelectMultiStyles, stateOptions } from 'Components'
import { PixelFormItem } from './shared'
import zips from 'Components/NewMover/ZipCodes.json'

export const PixelGeoLocationFilters = ({
  zipsFiltered,
  statesFiltered,
  zipsIncluded,
  statesIncluded,
  handleStatesFiltered,
  handleZipsFiltered,
  handleStatesIncluded,
  handleZipsIncluded,
  disabled,
}: {
  zipsFiltered: string[]
  statesFiltered: string[]
  zipsIncluded: string[]
  statesIncluded: string[]
  handleZipsFiltered: (zips: string[]) => void
  handleStatesFiltered: (states: string[]) => void
  handleZipsIncluded: (zips: string[]) => void
  handleStatesIncluded: (states: string[]) => void
  disabled?: boolean
}) => {
  const [showIncludeZips, setShowIncludeZips] = useState(
    zipsIncluded.length > 0,
  )
  const [showExcludeZips, setShowExcludeZips] = useState(
    zipsFiltered.length > 0,
  )
  const [showIncludeStates, setShowIncludeStates] = useState(
    statesIncluded.length > 0,
  )
  const [showExcludeStates, setShowExcludeStates] = useState(
    statesFiltered.length > 0,
  )

  // Validation logic to detect overlaps
  const zipOverlaps = zipsIncluded.filter((zip) => zipsFiltered.includes(zip))
  const stateOverlaps = statesIncluded.filter((state) =>
    statesFiltered.includes(state),
  )

  return (
    <div className="PixelGeoLocationFilters animate-slidedown flex flex-col gap-4">
      {/* ZIP Filters Section */}
      <div>
        <h3 className="mb-2 text-lg font-semibold">ZIP Filters</h3>

        {/* ZIP Overlap Alert */}
        {zipOverlaps.length > 0 && (
          <div className="border-warning bg-warning-50 mb-3 rounded-md border p-3">
            <div className="flex items-center">
              <div className="text-warning text-sm">
                <strong>Overlap detected:</strong> The following ZIP codes{' '}
                appear in both include and exclude lists:{' '}
                {zipOverlaps.join(', ')}
              </div>
            </div>
          </div>
        )}

        {/* Include ZIP Codes */}
        <div className="flex flex-col gap-2 py-2">
          <Toggle
            label="Include ZIP Codes"
            onChange={setShowIncludeZips}
            isChecked={showIncludeZips}
            isDisabled={disabled}
            className="!w-max !items-start"
          />
          {showIncludeZips && (
            <PixelFormItem label="" htmlFor="zipcodes-included">
              <TagInput
                value={zipsIncluded}
                addTagsInBulk
                validateFromSuggestions
                autoCompleteSuggestionsList={zips}
                maxAutoCompleteSuggestions={5}
                onAddTag={(tagArray: string[]) => handleZipsIncluded(tagArray)}
                onRemoveTag={(tag) =>
                  handleZipsIncluded(zipsIncluded.filter((zip) => zip !== tag))
                }
                validationPattern="^\d{5}$"
                tagErrorLabel="zip code"
                disabled={disabled}
              />
            </PixelFormItem>
          )}
        </div>

        {/* Exclude ZIP Codes */}
        <div className="flex flex-col gap-2 py-2">
          <Toggle
            label="Exclude ZIP Codes"
            onChange={setShowExcludeZips}
            isChecked={showExcludeZips}
            isDisabled={disabled}
            className="!w-max !items-start"
          />
          {showExcludeZips && (
            <PixelFormItem label="" htmlFor="zipcodes-filtered">
              <TagInput
                value={zipsFiltered}
                addTagsInBulk
                validateFromSuggestions
                autoCompleteSuggestionsList={zips}
                maxAutoCompleteSuggestions={5}
                onAddTag={(tagArray: string[]) => handleZipsFiltered(tagArray)}
                onRemoveTag={(tag) =>
                  handleZipsFiltered(zipsFiltered.filter((zip) => zip !== tag))
                }
                validationPattern="^\d{5}$"
                tagErrorLabel="zip code"
                disabled={disabled}
              />
            </PixelFormItem>
          )}
        </div>
      </div>

      {/* State Filters Section */}
      <div>
        <h3 className="mb-2 text-lg font-semibold">State Filters</h3>

        {/* State Overlap Alert */}
        {stateOverlaps.length > 0 && (
          <div className="border-warning bg-warning-50 mb-3 rounded-md border p-3">
            <div className="flex items-center">
              <div className="text-warning text-sm">
                <strong>Overlap detected:</strong> The following states appear
                in both include and exclude lists:{' '}
                {stateOverlaps
                  .map((stateAbbr) => {
                    const stateOption = stateOptions.find(
                      (option) => option.value === stateAbbr,
                    )
                    return stateOption ? stateOption.label : stateAbbr
                  })
                  .join(', ')}
              </div>
            </div>
          </div>
        )}

        {/* Include States */}
        <div className="flex flex-col gap-2 py-2">
          <Toggle
            label="Include States"
            onChange={setShowIncludeStates}
            isChecked={showIncludeStates}
            isDisabled={disabled}
            className="!w-max !items-start"
          />
          {showIncludeStates && (
            <PixelFormItem label="" htmlFor="states-included">
              <Select
                value={statesIncluded.reduce(
                  (acc: Array<{ label: string; value: string }>, stateAbbr) => {
                    if (stateAbbr) {
                      const stateOption = stateOptions.find(
                        (option) => option.value === stateAbbr,
                      )
                      if (stateOption) {
                        return [...acc, stateOption]
                      }
                    }
                    return acc
                  },
                  [],
                )}
                isMulti
                options={stateOptions}
                isClearable
                menuPortalTarget={document.body}
                placeholder="Select states to include..."
                className="py-2"
                classNamePrefix="GeoLocationStateSelect"
                onChange={(
                  selectedOptions: { label: string; value: string }[],
                ) => {
                  const selectedValues = selectedOptions?.map(
                    (option) => option.value,
                  )
                  handleStatesIncluded(selectedValues || [])
                }}
                components={{
                  DropdownIndicator: (props: DropdownIndicatorProps) => (
                    <components.DropdownIndicator {...props}>
                      <Fa icon="chevron-down" size={1} />
                    </components.DropdownIndicator>
                  ),
                  ClearIndicator: (props: ClearIndicatorProps) => (
                    <components.ClearIndicator {...props}>
                      <Fa icon="xmark" size={1} />
                    </components.ClearIndicator>
                  ),
                  MultiValueRemove: (props: MultiValueRemoveProps) => (
                    <components.MultiValueRemove {...props}>
                      <span
                        className="ml-2 cursor-pointer font-bold"
                        aria-label={`Remove ${
                          (props?.data as { label: string; value: string })
                            ?.label
                        }`}
                      >
                        ✕
                      </span>
                    </components.MultiValueRemove>
                  ),
                }}
                styles={reactSelectMultiStyles}
                isDisabled={disabled}
              />
            </PixelFormItem>
          )}
        </div>

        {/* Exclude States */}
        <div className="flex flex-col gap-2 py-2">
          <Toggle
            label="Exclude States"
            onChange={setShowExcludeStates}
            isChecked={showExcludeStates}
            isDisabled={disabled}
            className="!w-max !items-start"
          />
          {showExcludeStates && (
            <PixelFormItem label="" htmlFor="states-excluded">
              <Select
                value={statesFiltered.reduce(
                  (acc: Array<{ label: string; value: string }>, stateAbbr) => {
                    if (stateAbbr) {
                      const stateOption = stateOptions.find(
                        (option) => option.value === stateAbbr,
                      )
                      if (stateOption) {
                        return [...acc, stateOption]
                      }
                    }
                    return acc
                  },
                  [],
                )}
                isMulti
                options={stateOptions}
                isClearable
                menuPortalTarget={document.body}
                placeholder="Select states to exclude..."
                className="py-2"
                classNamePrefix="GeoLocationStateSelect"
                onChange={(
                  selectedOptions: { label: string; value: string }[],
                ) => {
                  const selectedValues = selectedOptions?.map(
                    (option) => option.value,
                  )
                  handleStatesFiltered(selectedValues || [])
                }}
                components={{
                  DropdownIndicator: (props: DropdownIndicatorProps) => (
                    <components.DropdownIndicator {...props}>
                      <Fa icon="chevron-down" size={1} />
                    </components.DropdownIndicator>
                  ),
                  ClearIndicator: (props: ClearIndicatorProps) => (
                    <components.ClearIndicator {...props}>
                      <Fa icon="xmark" size={1} />
                    </components.ClearIndicator>
                  ),
                  MultiValueRemove: (props: MultiValueRemoveProps) => (
                    <components.MultiValueRemove {...props}>
                      <span
                        className="ml-2 cursor-pointer font-bold"
                        aria-label={`Remove ${
                          (props?.data as { label: string; value: string })
                            ?.label
                        }`}
                      >
                        ✕
                      </span>
                    </components.MultiValueRemove>
                  ),
                }}
                styles={reactSelectMultiStyles}
                isDisabled={disabled}
              />
            </PixelFormItem>
          )}
        </div>
      </div>
    </div>
  )
}
